import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

function Login() {
  // "staff" (email+password via /token) or "user" (user_code+password via /user-token)
  const [mode, setMode] = useState("staff");
  const [username, setUsername] = useState(""); // email for staff, user_code for user
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const validateForm = () => {
    if (!username || !password) {
      setError(`${mode === "staff" ? "Email" : "User code"} and password are required`);
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError("");

    try {
      let token = "";
      let user_code = null;
      let staffRole = null;
      let crmUserId = null;
      let staffName = null;
      let staffEmail = null;

      if (mode === "staff") {
        // Existing staff login flow -> FastAPI /token (CRM)
        const formDetails = new URLSearchParams();
        formDetails.append("username", username); // email
        formDetails.append("password", password);

        const response = await fetch("/token", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: formDetails,
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.detail || "Authentication failed!");
        }
        token = data.access_token;
        staffRole = data.role || null;
        crmUserId = data.user_id != null ? String(data.user_id) : null;
        staffName =
          data.name ||
          data.full_name ||
          (username.includes("@") ? username.split("@")[0] : username) ||
          null;
        staffEmail = data.email || username;
      } else {
        // New USER login flow -> FastAPI /user-token (returns app-signed JWT)
        const response = await fetch("/user-token", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_code: username,
            password,
            device_name: "web",
          }),
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.detail || "Authentication failed!");
        }
        token = data.access_token;
        user_code = data.user_code || username;
      }

      // Persist auth
      localStorage.setItem("token", token);
      localStorage.setItem("role", mode); // "staff" or "user"
      if (staffRole) localStorage.setItem("staff_role", staffRole);
      if (crmUserId) localStorage.setItem("crm_user_id", crmUserId);
      if (staffName) localStorage.setItem("staff_name", staffName);
      if (staffEmail) localStorage.setItem("email", staffEmail);
      if (user_code) localStorage.setItem("user_code", user_code);

      // Route to correct dashboard
      navigate(mode === "user" ? "/user" : "/protected");
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "An error occurred. Please try again later.");
      // Clean any partial state on error
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem("staff_role");
      localStorage.removeItem("crm_user_id");
      localStorage.removeItem("staff_name");
      localStorage.removeItem("email");
      localStorage.removeItem("user_code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h2 className="card-title text-center mb-4">Login</h2>

              {/* Mode toggle */}
              <div className="mb-3 d-flex justify-content-center gap-2">
                <button
                  type="button"
                  className={`btn btn-sm ${mode === "staff" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => {
                    setMode("staff");
                    setError("");
                    setUsername("");
                    setPassword("");
                  }}
                >
                  Staff
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${mode === "user" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => {
                    setMode("user");
                    setError("");
                    setUsername("");
                    setPassword("");
                  }}
                >
                  User
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="username" className="form-label">
                    {mode === "staff" ? "Email" : "User Code"}:
                  </label>
                  <input
                    type={mode === "staff" ? "email" : "text"}
                    className="form-control"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete={mode === "staff" ? "email" : "username"}
                    placeholder={mode === "staff" ? "name@example.com" : "e.g. HD1"}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="password" className="form-label">
                    Password:
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="Your password"
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading ? "Logging in..." : "Login"}
                </button>

                {/* Link to user login */}
                <div className="text-center mt-3">
                  <small className="text-muted">
                    User? <a href="/userlogin">Login here</a>
                  </small>
                </div>

                {error && (
                  <div className="alert alert-danger mt-3" role="alert">
                    {error}
                  </div>
                )}
              </form>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
